import { UUID } from "crypto";

import { documentApiV2 } from "@perceptron/api/apiClient";
import { create } from "zustand";

export interface DocumentV2 {
  documentUuid: UUID;
  documentName: string;
  createdAt: Date;
}

interface DocumentMetadataStore {
  documentMetadata: DocumentV2[];
  activeDocumentUuid: UUID | null;
  isLoadingDocument: UUID | null;
  isLoadingDocumentMetadata: boolean;
  isAppFirstLoading: boolean;
  setDocumentMetadata: (metadata: DocumentV2[]) => void;
  addDocumentMetadataEntry: (metadata: DocumentV2) => void;
  setIsAppFirstLoading: (isAppFirstLoading: boolean) => void;
  setActiveDocument: (documentUuid: UUID | null) => void;
  fetchDocumentMetadata: () => Promise<void>;
  clearActiveDocument: () => void;
  setIsLoadingDocument: (documentUuid: UUID | null) => void;
  deleteDocumentMetadata: (documentUuid: UUID) => void;
  renameDocument: (documentUuid: UUID, newName: string) => Promise<void>;
}

export const useDocumentMetadataStore = create<DocumentMetadataStore>((set, _get) => ({
  documentMetadata: [],
  activeDocumentUuid: null,
  isLoadingDocument: null,
  isLoadingDocumentMetadata: true,
  isAppFirstLoading: true,

  setDocumentMetadata: (metadata) => set({ documentMetadata: metadata }),

  addDocumentMetadataEntry: (metadata) =>
    set((state) => ({
      documentMetadata: [metadata, ...state.documentMetadata], // we order by most recently created first
    })),

  setActiveDocument: (documentUuid) => {
    set({ activeDocumentUuid: documentUuid });
  },

  setIsLoadingDocument: (documentUuid) => set({ isLoadingDocument: documentUuid }),

  setIsAppFirstLoading: (isAppFirstLoading: boolean) => set({ isAppFirstLoading }),

  fetchDocumentMetadata: async () => {
    const loadDocumentMetadata = async () => {
      set({ isLoadingDocumentMetadata: true });
      const response = await documentApiV2.getDocuments();
      const metadata = response.documents;
      // If there is no document metadata, then we've finished loading the app
      // and do not have a first document to load.
      if (metadata.length === 0) {
        set({ isAppFirstLoading: false });
      }
      set({
        documentMetadata: metadata.map((doc) => ({
          documentUuid: doc.documentUuid,
          documentName: doc.documentName,
          createdAt: doc.createdAt,
        })),
        isLoadingDocumentMetadata: false,
      });
      return metadata;
    };

    try {
      await loadDocumentMetadata();
    } catch (error) {
      console.error("Error fetching document metadata:", error);
    } finally {
      set({ isLoadingDocumentMetadata: false });
    }
  },

  clearActiveDocument: () => {
    set({ activeDocumentUuid: null });
  },

  deleteDocumentMetadata: (documentUuid) => {
    const getNextDocumentUuid = (state: DocumentMetadataStore): UUID | null => {
      if (documentUuid !== state.activeDocumentUuid) {
        return state.activeDocumentUuid;
      }

      const filteredDocumentMetadata = state.documentMetadata.filter((doc) => doc.documentUuid !== documentUuid);

      if (filteredDocumentMetadata.length > 0) {
        return filteredDocumentMetadata[0]!.documentUuid;
      } else {
        return null;
      }
    };

    documentApiV2.deleteDocument(documentUuid);

    set((state) => ({
      documentMetadata: state.documentMetadata.filter((doc) => doc.documentUuid !== documentUuid),
      activeDocumentUuid: getNextDocumentUuid(state),
    }));
  },

  renameDocument: async (documentUuid, newName) => {
    // Get the current document to store the original name
    const state = useDocumentMetadataStore.getState();
    const originalDocument = state.documentMetadata.find((doc) => doc.documentUuid === documentUuid);

    if (!originalDocument) {
      console.error("Document not found for rename:", documentUuid);
      throw new Error("Document not found");
    }

    const originalName = originalDocument.documentName;

    // Optimistically update the UI
    set((state) => ({
      documentMetadata: state.documentMetadata.map((doc) =>
        doc.documentUuid === documentUuid ? { ...doc, documentName: newName } : doc,
      ),
    }));

    try {
      // Attempt to update on the backend
      const newMetadata = await documentApiV2.updateDocument(documentUuid, { documentName: newName });

      // Update the document metadata in the store with the result from the backend
      set((state) => ({
        documentMetadata: state.documentMetadata.map((doc) =>
          doc.documentUuid === documentUuid ? { ...doc, documentName: newMetadata.document.documentName } : doc,
        ),
      }));
    } catch (error) {
      // Revert to original name on failure
      console.error("Error renaming document:", error);
      set((state) => ({
        documentMetadata: state.documentMetadata.map((doc) =>
          doc.documentUuid === documentUuid ? { ...doc, documentName: originalName } : doc,
        ),
      }));
      throw error;
    }
  },
}));
